import { useMemo, type JSX } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { IUserCompanyData } from '~/@api/generated-api-types-user/Api'
import { Spinner } from '~/shadcn/ui/spinner'
import { Input } from '~/shadcn/ui/input'
import { Checkbox } from '~/shadcn/ui/form/checkbox'
import { Form, FormField, FormItem } from '~/shadcn/ui/form/form'
import {
  FieldLabel,
  FieldError,
  FieldGroup,
  FieldErrorBlank,
} from '~/shadcn/ui/field'
import { Label } from '~/shadcn/ui/label'
import { CountrySelect } from '~/@mid_ui-form/country-select/CountrySelect'
import { RequiredLabel } from '~/@mid_ui-form/required-label/RequiredLabel'
import { HeadingFive } from '~/@mid_ui/typography/HeadingFive'
import { FormError } from '~/@mid_ui-form/form-error/FormError'
import { projectTooltipByType } from '~/@pages_investing/projects/_projects_ui/project-info-tooltips/projectTooltipByType'
import { Button } from '~/shadcn/ui/button/button'
import {
  companyConfirmationSchema,
  type CompanyConfirmationSchema,
} from './companyConfirmationSchema'

type Props = {
  defaultValues?: IUserCompanyData
  onSubmit(formData: IUserCompanyData): void
  isRequesting: boolean
  error: string | null
  CancelButton?: JSX.Element
}

export function CompanyRegistrationForm({
  defaultValues,
  onSubmit,
  error,
  isRequesting,
  CancelButton,
}: Props) {
  const form = useForm<CompanyConfirmationSchema>({
    resolver: zodResolver(companyConfirmationSchema),
    defaultValues: defaultValues ?? {
      name: '',
      id: '',
      vat: '',
      vatNumber: '',
      isSoleTrader: false,
      country: '',
      streetAddress: '',
      zipCode: '',
      city: '',
    },
  })

  const haveErrorsInIdOrVat = useMemo(
    () =>
      Object.keys(form.formState.errors).some((key) =>
        ['id', 'vat'].includes(key)
      ),
    [form.formState.errors]
  )
  const haveErrorsInZipOrCity = useMemo(
    () =>
      Object.keys(form.formState.errors).some((key) =>
        ['zipCode', 'city'].includes(key)
      ),
    [form.formState.errors]
  )

  const companyName = useWatch({ control: form.control, name: 'name' })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) =>
          onSubmit(data as IUserCompanyData)
        )}>
        <div className="flex flex-col gap-12">
          <HeadingFive>Vyplňte vaše firemné údaje</HeadingFive>

          <FieldGroup>
            <FormField
              control={form.control}
              name="name"
              rules={{ required: 'Toto pole je povinné.' }}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FieldLabel>
                    <RequiredLabel text="Názov spoločnosti / Obchodné meno" />
                  </FieldLabel>
                  <Input
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    aria-invalid={!!fieldState.error}
                  />
                  {fieldState.error && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </FormItem>
              )}
            />

            <div className="flex flex-row gap-2">
              <FormField
                control={form.control}
                name="id"
                rules={{ required: 'Toto pole je povinné.' }}
                render={({ field, fieldState }) => (
                  <FormItem className="flex-1">
                    <FieldLabel>
                      <RequiredLabel text="IČO" />
                    </FieldLabel>
                    <Input
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      aria-invalid={!!fieldState.error}
                    />
                    {fieldState.error ? (
                      <FieldError>{fieldState.error.message}</FieldError>
                    ) : (
                      haveErrorsInIdOrVat && <FieldErrorBlank />
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="vat"
                rules={{ required: 'Toto pole je povinné.' }}
                render={({ field, fieldState }) => (
                  <FormItem className="flex-1">
                    <FieldLabel>
                      <RequiredLabel text="DIČ" />
                    </FieldLabel>
                    <Input
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      aria-invalid={!!fieldState.error}
                    />
                    {fieldState.error ? (
                      <FieldError>{fieldState.error.message}</FieldError>
                    ) : (
                      haveErrorsInIdOrVat && <FieldErrorBlank />
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="vatNumber"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FieldLabel>IČ DPH</FieldLabel>
                    <Input
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                    />
                    {haveErrorsInIdOrVat && <FieldErrorBlank />}
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="isSoleTrader"
              rules={{ required: 'Toto pole je povinné.' }}
              render={({ field, fieldState }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={field.value ?? false}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                      id="isSoleTrader"
                    />
                    <Label
                      htmlFor="isSoleTrader"
                      className="flex items-center gap-1 font-semibold text-base cursor-pointer">
                      <span>
                        Som živnostník/konateľ spoločnosti „
                        {companyName || 'názov firmy'}"
                      </span>
                      {projectTooltipByType.companyRegisterRequirement}
                    </Label>
                  </div>
                  {fieldState.error && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </FormItem>
              )}
            />

            <CountrySelect<CompanyConfirmationSchema> control={form.control} />

            <FormField
              control={form.control}
              name="streetAddress"
              rules={{ required: 'Toto pole je povinné.' }}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FieldLabel>
                    <RequiredLabel text="Sídlo (ulica a číslo domu)" />
                  </FieldLabel>
                  <Input
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                    aria-invalid={!!fieldState.error}
                  />
                  {fieldState.error && (
                    <FieldError>{fieldState.error.message}</FieldError>
                  )}
                </FormItem>
              )}
            />

            <div className="flex flex-row gap-2">
              <FormField
                control={form.control}
                name="zipCode"
                rules={{ required: 'Toto pole je povinné.' }}
                render={({ field, fieldState }) => (
                  <FormItem className="flex-[0.3]">
                    <FieldLabel>
                      <RequiredLabel text="PSČ" />
                    </FieldLabel>
                    <Input
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      aria-invalid={!!fieldState.error}
                    />
                    {fieldState.error ? (
                      <FieldError>{fieldState.error.message}</FieldError>
                    ) : (
                      haveErrorsInZipOrCity && <FieldErrorBlank />
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="city"
                rules={{ required: 'Toto pole je povinné.' }}
                render={({ field, fieldState }) => (
                  <FormItem className="flex-[0.7]">
                    <FieldLabel>
                      <RequiredLabel text="Mesto" />
                    </FieldLabel>
                    <Input
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      aria-invalid={!!fieldState.error}
                    />
                    {fieldState.error ? (
                      <FieldError>{fieldState.error.message}</FieldError>
                    ) : (
                      haveErrorsInZipOrCity && <FieldErrorBlank />
                    )}
                  </FormItem>
                )}
              />
            </div>
          </FieldGroup>

          <div className="flex flex-row gap-2 justify-between">
            {CancelButton ?? null}

            <Button
              variant="accent"
              type="submit"
              size="lg"
              disabled={
                isRequesting || Object.keys(form.formState.errors).length > 0
              }>
              Potvrdiť{` `}
              {isRequesting && <Spinner className="size-4" />}
            </Button>
          </div>

          {error && <FormError error={error} />}
        </div>
      </form>
    </Form>
  )
}
